import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { errMsg } from '../api';
import { formatUGX } from '../mock';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Search, Pill, Plus, Loader2 } from 'lucide-react';

const CATS = ['Antibiotic', 'Analgesic', 'Antimalarial', 'Antidiabetic', 'Supplement', 'Rehydration', 'Antacid', 'Antihistamine', 'Vitamin', 'Other'];

const DrugCatalogPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const canManage = user?.role === 'pharmacy_admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', generic: '', form: 'Tablet', category: 'Analgesic', price: '', reorder_level: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/products')).data); }
    catch (e) { toast({ title: 'Could not load catalog', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.price) { toast({ title: 'Missing info', description: 'Name and price are required.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await api.post('/products', {
        name: form.name, generic: form.generic, form: form.form, category: form.category,
        price: Number(form.price), reorder_level: Number(form.reorder_level) || 0,
      });
      toast({ title: 'Drug added', description: `${form.name} added to catalog.` });
      setForm({ name: '', generic: '', form: 'Tablet', category: 'Analgesic', price: '', reorder_level: '' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Could not add drug', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const filtered = items.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()) || (p.generic || '').toLowerCase().includes(query.toLowerCase()));

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Drug Catalog</h1>
          <p className="text-slate-500 mt-2 text-[15px]">The products your pharmacy stocks, with live available stock.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11 px-5 font-semibold gap-2"><Plus size={18} /> Add drug</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-xl">Add a drug</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label className="text-slate-700">Product name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Amoxicillin 250mg" className="mt-1.5 h-11" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-700">Generic</Label><Input value={form.generic} onChange={(e) => setForm({ ...form, generic: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div><Label className="text-slate-700">Form</Label><Input value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-700">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-slate-700">Price (UGX)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div><Label className="text-slate-700">Reorder level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
                <p className="text-slate-400 text-xs">New drugs start at zero stock — add stock via Inventory → Restock.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={add} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Saving…' : 'Add drug'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-md mt-6">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search drug or generic..." className="pl-11 h-11 rounded-xl bg-white border-slate-200" />
      </div>

      {loading ? <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4">
          {filtered.map((d) => (
            <div key={d.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Pill size={18} /></div>
                <Badge variant="outline" className="text-slate-500 border-slate-200">{d.form}</Badge>
              </div>
              <div className="font-display font-bold text-slate-900 mt-3">{d.name}</div>
              <div className="text-slate-400 text-sm mt-0.5">{d.generic} · {d.category}</div>
              <div className="flex items-center justify-between mt-4">
                <span className="font-display font-bold text-blue-600">{formatUGX(d.price)}</span>
                <span className={`text-sm ${d.available <= 0 ? 'text-slate-400' : d.low_stock ? 'text-amber-600' : 'text-slate-500'}`}>Stock: {d.available}</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center text-slate-400 py-16">No drugs found.</div>}
        </div>
      )}
    </div>
  );
};

export default DrugCatalogPage;
